import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  Bookmark,
  BookmarkCheck,
  ChevronDown,
  ChevronUp,
  NotebookPen,
  X,
} from "lucide-react";
import {
  saveItem,
  studyItemKey,
  toggleSaved,
  useMyStudy,
} from "../../utils/myStudy";
import StudyNoteField from "./StudyNoteField";
import { itemSubtitle, itemTitle } from "../../utils/notesExport";
import { needsAccountToSave, openAccount } from "../../firebase/account";

/*
 * Saving needs an account, so the study is there on any device. A guest who
 * presses Save is asked to create one or sign in, and the Save goes through
 * as soon as they have. Removing something already saved in this browser
 * (from before accounts) is left alone.
 */
function pressSave(item, saved) {
  if (!saved && needsAccountToSave()) {
    openAccount({
      mode: "signUp",
      reason:
        "Create a free account to save to My Study. Your saves and notes will be there on any device you sign in on.",
      then: () => saveItem(item),
    });
    return;
  }

  toggleSaved(item);
}

/**
 * Saves the given person, page or passage to My Study, or removes it -- and,
 * once saved, offers a note on it without leaving the page.
 *
 * `item` is the record as My Study stores it: { type: "person", id, name, ... },
 * { type: "page", path, title }, or { type: "passage", reference }.
 *
 * aria-pressed rather than a label that flips between "Save" and "Remove": the
 * button is a toggle, and a screen reader announcing it as one says both what
 * it does and which state it is in.
 *
 * The note lives in a dock fixed to the window, so it stays with the reader
 * as they scroll the page they are writing about. On desktop it is a floating
 * card on the right, and once the item is saved a "Notes" tab on that edge
 * brings it back. On a phone it is a sheet that rises from the bottom over
 * half the screen, leaving the page in view above it.
 *
 * On a phone the button beside the title is gone once the reader scrolls, so
 * there Save and Notes float in the bottom-right corner instead, always in
 * reach; the stylesheet hides the inline buttons at that width.
 *
 * The floating buttons are portalled to <body>, because the hero and the
 * Study Guide bar both clip their overflow. The notes dock goes to <html>
 * itself, beside the verse modal's own portal: while that modal is open it
 * pins <body> with position: fixed, which makes the whole body one stacking
 * layer underneath the modal, so a note on a passage hid behind the passage
 * however high its z-index.
 *
 * `showTab` is off for the verse modal's button: the page underneath already
 * owns the edge tab and the floating buttons, and two would stack.
 */
export default function SaveToStudyButton({
  item,
  className = "",
  compact = false,
  showTab = true,
}) {
  const { items } = useMyStudy();
  const key = studyItemKey(item);
  const saved = key ? items.find((entry) => entry.key === key) : null;
  const [noteOpen, setNoteOpen] = useState(false);
  const [tucked, setTucked] = useState(false);
  const dockRef = useRef(null);
  const panelRef = useRef(null);
  const swipeRef = useRef(null);

  /* Unsaving removes the note with the item, so the dock has nothing left to
     edit. */
  useEffect(() => {
    if (!saved) setNoteOpen(false);
  }, [saved]);

  /* A note always opens in full; tucking is only for the stretch in between. */
  useEffect(() => {
    if (!noteOpen) setTucked(false);
  }, [noteOpen]);

  /* Escape closes. Clicking elsewhere does not: the point of the dock is to
     keep writing while reading and scrolling the page beside it. */
  useEffect(() => {
    if (!noteOpen) return undefined;

    function handleKey(event) {
      if (event.key === "Escape") setNoteOpen(false);
    }

    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [noteOpen]);

  /* On a phone the keyboard takes the bottom half of the screen. While the
     note is open, the dock is sized to what the keyboard leaves -- the
     visual viewport -- so the sheet at the top never runs underneath it.
     iOS also scrolls the page under a focused field, which offsetTop
     follows. */
  useEffect(() => {
    const viewport = typeof window !== "undefined" ? window.visualViewport : null;
    const dock = dockRef.current;
    if (!noteOpen || !viewport || !dock) return undefined;

    function fit() {
      dock.style.setProperty("--ctb-vv-top", `${viewport.offsetTop}px`);
      dock.style.setProperty("--ctb-vv-height", `${viewport.height}px`);
    }

    fit();
    viewport.addEventListener("resize", fit);
    viewport.addEventListener("scroll", fit);

    return () => {
      viewport.removeEventListener("resize", fit);
      viewport.removeEventListener("scroll", fit);
      dock.style.removeProperty("--ctb-vv-top");
      dock.style.removeProperty("--ctb-vv-height");
    };
  }, [noteOpen]);

  if (!key) return null;

  /*
   * Tucking shrinks the note to a bar across the top of the screen (on a
   * desktop, to its header), so the reader can go back to the page or the
   * modal underneath and return to the note without closing it. The note
   * stays mounted throughout -- nothing is saved and reloaded in between --
   * and the keyboard is put away while it is tucked so the page can be read.
   */
  function tuck() {
    const active = document.activeElement;
    if (active && panelRef.current?.contains(active)) active.blur();
    setTucked(true);
  }

  function untuck() {
    setTucked(false);
    window.setTimeout(() => {
      panelRef.current?.querySelector("textarea")?.focus();
    }, 260);
  }

  /* A swipe up on the sheet's header or handle tucks it, a swipe down brings
     it back, as with any sheet on a phone. */
  const swipeHandlers = {
    onTouchStart: (event) => {
      swipeRef.current = event.touches[0]?.clientY ?? null;
    },
    onTouchEnd: (event) => {
      const start = swipeRef.current;
      swipeRef.current = null;
      const end = event.changedTouches[0]?.clientY;
      if (start == null || end == null) return;
      if (end - start < -30 && !tucked) tuck();
      else if (end - start > 30 && tucked) untuck();
    },
  };

  const preview = String(saved?.note || "").trim().split("\n")[0];

  const Icon = saved ? BookmarkCheck : Bookmark;
  const hasNote = Boolean(saved?.note?.trim());
  const label = itemTitle(item);

  /* Under the name in the notes dock, so it is clear whose note this is when
     two people share a name: "David" / "King of Israel". */
  const subtitle = itemSubtitle(item);
  const showDock = showTab || (saved && noteOpen);

  return (
    <span
      className={`ctb-saveToStudyWrap ${showTab ? "has-fab" : ""} ${className}`}
    >
      <button
        type="button"
        className={`ctb-saveToStudy ${saved ? "is-saved" : ""} ${
          compact ? "ctb-saveToStudy--compact" : ""
        }`}
        aria-pressed={Boolean(saved)}
        title={saved ? "Saved to My Study — click to remove" : "Save to My Study"}
        onClick={() => pressSave(item, saved)}
      >
        <Icon size={16} aria-hidden="true" />
        <span className="ctb-saveToStudy-label">
          {compact
            ? saved
              ? "Saved"
              : "Save"
            : saved
            ? "Saved to My Study"
            : "Save to My Study"}
        </span>
      </button>

      {saved ? (
        <button
          type="button"
          className={`ctb-saveToStudy-note ${hasNote ? "has-note" : ""} ${
            noteOpen ? "is-open" : ""
          }`}
          aria-expanded={noteOpen}
          aria-label={`${hasNote ? "Edit" : "Add"} note for ${label}`}
          title={hasNote ? "Edit your note" : "Add a note"}
          onClick={() => setNoteOpen((open) => !open)}
        >
          <NotebookPen size={15} aria-hidden="true" />
          {hasNote ? (
            <span className="ctb-saveToStudy-noteDot" aria-hidden="true" />
          ) : null}
        </button>
      ) : null}

      {/* Its own layer, under the site's modals (1050 and up), so it never
          covers a modal's close or next buttons; the notes dock above stays
          over the verse modal for a passage note. */}
      {showTab && !noteOpen
        ? createPortal(
            <div className="ctb-studyFab">
              {saved ? (
                <button
                  type="button"
                  className="ctb-studyFab-notes"
                  aria-label={`${hasNote ? "Edit" : "Add"} note for ${label}`}
                  onClick={() => setNoteOpen(true)}
                >
                  <NotebookPen size={18} aria-hidden="true" />
                  {hasNote ? (
                    <span
                      className="ctb-saveToStudy-noteDot"
                      aria-hidden="true"
                    />
                  ) : null}
                </button>
              ) : null}

              <button
                type="button"
                className={`ctb-studyFab-save ${saved ? "is-saved" : ""}`}
                aria-pressed={Boolean(saved)}
                aria-label={
                  saved
                    ? `${label} is saved to My Study. Remove it`
                    : `Save ${label} to My Study`
                }
                onClick={() => pressSave(item, saved)}
              >
                <Icon size={18} aria-hidden="true" />
                <span>{saved ? "Saved" : "Save"}</span>
              </button>
            </div>,
            document.body
          )
        : null}

      {showDock
        ? createPortal(
            <div
              ref={dockRef}
              className={`ctb-notesDock ${noteOpen ? "is-open" : ""}`}
            >
              {saved && showTab && !noteOpen ? (
                <button
                  type="button"
                  className="ctb-notesDock-tab"
                  aria-label={`Open note for ${label}`}
                  onClick={() => setNoteOpen(true)}
                >
                  <NotebookPen size={13} aria-hidden="true" />
                  <span>Notes</span>
                  {hasNote ? (
                    <span
                      className="ctb-saveToStudy-noteDot"
                      aria-hidden="true"
                    />
                  ) : null}
                </button>
              ) : null}

              <section
                ref={panelRef}
                className={`ctb-notesDock-panel ${tucked ? "is-tucked" : ""}`}
                aria-label={`Note for ${label}`}
                aria-hidden={!noteOpen}
              >
                <div className="ctb-notesDock-head" {...swipeHandlers}>
                  <div
                    className="ctb-notesDock-titles"
                    onClick={tucked ? untuck : undefined}
                  >
                    <span className="ctb-notesDock-eyebrow">Your note</span>
                    <span className="ctb-notesDock-title">{label}</span>
                    {tucked ? (
                      <span className="ctb-notesDock-preview">
                        {preview || "No note yet"}
                      </span>
                    ) : subtitle ? (
                      <span className="ctb-notesDock-subtitle">
                        {subtitle}
                      </span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="ctb-notesDock-close"
                    aria-label={tucked ? "Show note" : "Tuck note away"}
                    aria-expanded={!tucked}
                    title={tucked ? "Show note" : "Tuck away"}
                    tabIndex={noteOpen ? 0 : -1}
                    onClick={tucked ? untuck : tuck}
                  >
                    {tucked ? (
                      <ChevronDown size={16} aria-hidden="true" />
                    ) : (
                      <ChevronUp size={16} aria-hidden="true" />
                    )}
                  </button>
                  <button
                    type="button"
                    className="ctb-notesDock-close"
                    aria-label="Close note"
                    tabIndex={noteOpen ? 0 : -1}
                    onClick={() => setNoteOpen(false)}
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                </div>

                {/* Mounted only while open, so closing flushes the text
                    through StudyNoteField's unmount save. */}
                {noteOpen && saved ? (
                  <div className="ctb-notesDock-body" hidden={tucked}>
                    <StudyNoteField
                      itemKey={key}
                      note={saved.note}
                      label={label}
                    />

                    <Link
                      className="ctb-notesDock-link"
                      to="/my-study"
                      onClick={() => setNoteOpen(false)}
                    >
                      All notes in My Study →
                    </Link>
                  </div>
                ) : null}

                {noteOpen ? (
                  <button
                    type="button"
                    className="ctb-notesDock-handle"
                    aria-label={tucked ? "Show note" : "Tuck note away"}
                    onClick={tucked ? untuck : tuck}
                    {...swipeHandlers}
                  />
                ) : null}
              </section>
            </div>,
            document.documentElement
          )
        : null}
    </span>
  );
}
